import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

/**
 * Layout for placing the worker signature on `src/assets/pdfs/worker-agreement.pdf`.
 * Signature is drawn on the last page, below the contract content (bottom-left).
 * Coordinates use pdf-lib’s PDF space: origin bottom-left, points (1/72 inch).
 */
export const WORKER_AGREEMENT_SIGNATURE = {
  fileName: "signed-worker-agreement.pdf",
  /** 0-based page index; use -1 for the last page. */
  pageIndex: -1,
  marginLeftPt: 56,
  marginBottomPt: 48,
  maxWidthPt: 220,
  maxHeightPt: 80,
  signatureLabel: "Signature",
};

/** @deprecated Use WORKER_AGREEMENT_SIGNATURE */
export const WORKER_CONTRACT_SIGNATURE = WORKER_AGREEMENT_SIGNATURE;

/**
 * @param {string} url Vite asset URL (`?url`) or public path
 * @returns {Promise<ArrayBuffer>}
 */
export async function fetchPdfAsArrayBuffer(url) {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch PDF template: ${res.status}`);
  }
  return res.arrayBuffer();
}

/**
 * Merge a signature canvas onto the contract template (bottom-left on the chosen page).
 * @param {HTMLCanvasElement | null} signatureCanvas
 * @param {ArrayBuffer} templateBuffer
 * @param {Partial<typeof WORKER_AGREEMENT_SIGNATURE> & { fileName?: string }} [opts]
 * @returns {Promise<File | null>}
 */
export async function buildSignedContractPdf(signatureCanvas, templateBuffer, opts = {}) {
  if (!signatureCanvas?.width || !signatureCanvas?.height || !templateBuffer?.byteLength) {
    return null;
  }

  const {
    fileName = WORKER_AGREEMENT_SIGNATURE.fileName,
    pageIndex = WORKER_AGREEMENT_SIGNATURE.pageIndex,
    marginLeftPt = WORKER_AGREEMENT_SIGNATURE.marginLeftPt,
    marginBottomPt = WORKER_AGREEMENT_SIGNATURE.marginBottomPt,
    maxWidthPt = WORKER_AGREEMENT_SIGNATURE.maxWidthPt,
    maxHeightPt = WORKER_AGREEMENT_SIGNATURE.maxHeightPt,
    signatureLabel = WORKER_AGREEMENT_SIGNATURE.signatureLabel,
  } = opts;

  const pngDataUrl = signatureCanvas.toDataURL("image/png", 1);
  const pngRes = await fetch(pngDataUrl);
  const pngBytes = new Uint8Array(await pngRes.arrayBuffer());

  const pdfDoc = await PDFDocument.load(templateBuffer);
  const pngImage = await pdfDoc.embedPng(pngBytes);

  const pageCount = pdfDoc.getPageCount();
  const idx = pageIndex < 0 ? pageCount + pageIndex : pageIndex;
  if (idx < 0 || idx >= pageCount) {
    return null;
  }

  const page = pdfDoc.getPage(idx);
  const { width: pageW } = page.getSize();

  const iw = pngImage.width;
  const ih = pngImage.height;
  const aspect = iw / ih;

  const rightPadPt = 24;
  let w = Math.min(maxWidthPt, pageW - marginLeftPt - rightPadPt);
  let h = w / aspect;
  if (h > maxHeightPt) {
    h = maxHeightPt;
    w = h * aspect;
  }

  const x = marginLeftPt;
  const y = marginBottomPt;

  if (signatureLabel) {
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    page.drawText(signatureLabel, {
      x,
      y: y + h + 8,
      size: 10,
      font,
      color: rgb(0, 0, 0),
    });
  }

  page.drawImage(pngImage, { x, y, width: w, height: h });

  const outBytes = await pdfDoc.save();
  return new File([outBytes], fileName, { type: "application/pdf" });
}
